<template>
  <div class="container">
    <div class="checkout">
      <h1 class="h2">
        Checkout
      </h1>
      <div class="tabs">
        <div class="tab" :class="{ active: currentTab === 'account' }">
          <span>1</span>
          Account
        </div>
        <div class="progress-bar" :class="{ full: currentTab === 'payment' }">
          <div />
        </div>
        <div class="tab" :class="{ active: currentTab === 'payment' }">
          <span>2</span>
          Payment
        </div>
      </div>
      <div class="row">
        <div class="col col-8 steps-holder" :class="[currentTab, order.upgrade || order.extend ? 'verified' : '']">
          <div class="step" :class="{ active: currentTab === 'account', 'verified': accountVerified }" @click="changeTab('account')">
            <div class="heading">
              <h2 class="h5">
                <template v-if="accountVerified">
                  <div class="icon-check">
                    <IconCheck />
                  </div>
                  {{ `1. Account Details - ${$store.state.auth.user.name}` }}
                </template>
                <template v-else>
                  1. Account details
                </template>
              </h2>
              <div v-if="!loginForm">
                Already have an account? <a class="link-accent" @click="loginForm = true, $store.commit('auth/setLoginAlert', false)">
                  Login
                </a>
              </div>
              <div v-else>
                Don't have an account? <a class="link-accent" @click="loginForm = false, $store.commit('auth/setLoginAlert', false)">
                  Sign up
                </a>
              </div>
            </div>
            <div class="content">
              <AccountForm
                v-if="!loginForm"
                @continue="loggedSuccessfully"
              />
              <LoginForm v-else @continue="loggedSuccessfully" />
            </div>
          </div>
          <div
            class="step"
            :class="{
              active: currentTab === 'payment' && ((!order.upgrade && !order.extend && !paymentProfile) || paymentInfo.edit),
              verified: order.upgrade || order.extend || paymentProfile
            }"
            @click="changeTab('payment')"
          >
            <div class="heading">
              <h2 class="h5" :class="[paymentProfile && paymentInfo.edit ? 'back-button' : '']">
                <div v-if="(order.upgrade || paymentProfile) && !paymentInfo.edit" class="icon-check">
                  <IconCheck />
                </div>
                2. Payment

                <span v-if="paymentProfile && paymentInfo.edit" @click="$store.commit('checkout/setPaymentInfoEdit', false)">
                  Go back
                </span>
              </h2>
              <div v-if="!paymentProfile || paymentProfile && paymentInfo.edit" class="payment-icons">
                <IconVisa />
                <IconMastercard />
                <IconAmericanExpress />
                <span class="paypal"><IconPaypal /></span>
              </div>
            </div>
            <div v-if="paymentProfile && !paymentInfo.edit" class="payment-verified">
              <div class="card">
                <template v-if="paymentInfo.method === 'card'">
                  <component :is="cardIcon" />
                  {{ maskedCardNumber }}
                </template>
                <template v-else-if="paymentInfo.method === 'pay_pal'">
                  <IconPaypalLogo /> PayPal
                </template>
              </div>
              <span @click="$store.commit('checkout/setPaymentInfoEdit', true)">
                Change
              </span>
            </div>
            <div class="content">
              <Payment class="payment" />
            </div>
          </div>
          <div v-if="order.coins || (order.upgrade || order.extend || order.changeAllocation || (order.business && !order.trial)) && !paymentInfo.edit" class="important-information" :class="{ coins: order.coins }">
            <h2 class="h5">
              Buy More, Save More
            </h2>
            <div v-if="order.coins" class="coins-info">
              <p class="body-2">
                We offer а volume-based pricing system that gives you a more significant discount when buying coins in bulk. The more coins you buy, the lower the price per coin. Please allow some time for your coin purchases to appear in your account.
              </p>

              <h3 class="h6">
                Tiered coin pricing
              </h3>

              <ol class="price-list">
                <li>
                  <div>1 - 9 coins: <span>$1</span> each</div>
                </li>
                <li>
                  <div>10 - 24 coins: <span>$0.9</span> each</div>
                </li>
                <li>
                  <div>25 - 49 coins: <span>$0.8</span> each</div>
                </li>
                <li>
                  <div>50 - 99 coins: <span>$0.7</span> each</div>
                </li>
                <li>
                  <div>100+ coins: <span>$0.6</span> each</div>
                </li>
              </ol>
            </div>
            <div v-else-if="order.changeAllocation" class="allocation-change-info">
              <p class="body-2">
                Tiered pricing will provide you with automated discount for the higher number of subscriptions you add. The price of each subscription is assessed within its tier. For example, buying 3 subscriptions will cost ${{ (2 * order.prices[0].price) + order.prices[1].price }} (2*{{ order.prices[0].priceFormatted }} + 1*{{ order.prices[1].priceFormatted }}). <br> <br>
                <span v-for="(tier, k) in order.prices" :key="k">
                  {{ tier.startingQuantity }}-{{ tier.endingQuantity }} Subscriptions <strong>{{ tier.priceFormatted }}</strong> each <br>
                </span>
              </p>
              <h2 class="h5">
                Current Subscriptions information
              </h2>
              <p class="body-2">
                Proration will further reduce the prices of new subscriptions to match the remaining period of your current ones.
              </p>
              <p class="body-2 current-subscriptions">
                Number of current subscriptions: <span>{{ order.currentQuantity }}</span>
              </p>
            </div>
            <p v-else-if="order.business && !order.trial" class="body-2">
              Tiered pricing will provide you with automated discount for the higher number of subscriptions you add. The price of each subscription is assessed within its tier. For example, buying 3 subscriptions will cost ${{ (2 * order.prices[0].price) + order.prices[1].price }} (2*{{ order.prices[0].priceFormatted }} + 1*{{ order.prices[1].priceFormatted }}). <br> <br>
              <span v-for="(tier, k) in order.prices" :key="k">
                {{ tier.startingQuantity }}-{{ tier.endingQuantity }} Subscriptions <strong>{{ tier.priceFormatted }}</strong> each <br>
              </span>
            </p>

            <p v-else class="body-2">
              {{ order.extend
                ? `Your subscription will be extended for a period of ${order.plan.period} ${order.plan.period === 1 ? 'month' : 'months'} immediately after you complete this payment.`
                : `Your subscription will be upgraded to the ${order.plan.name} immediately after you complete this payment.` }}
            </p>
          </div>
          <Loader v-if="loadingChargify" type="element" class="payment-form-loader" />
          <SecureIcons class="secure-icons" />
        </div>
        <div class="col col-4">
          <OrderSummary v-if="order.preview" @expand="orderSummaryExpanded" />
          <Badges
            v-if="$store.state.layout !== 'mobile' || currentTab !== 'account'"
            class="checkout-badges"
            :hide-money-back="order.coins"
            info-icon="question"
          />
          <SecureIcons class="secure-icons" class-name="mobile" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import Badges from '@itbpbg/lms-components/src/Pricing/Badges';
import AccountForm from './AccountForm';
import LoginForm from './LoginForm';
import Payment from './Payment';
import OrderSummary from './OrderSummary';
import IconCheck from '../../assets/svg/icon-check.svg?vue-component';
import IconVisa from '../../assets/svg/icon-payment-card-visa.svg?vue-component';
import IconMastercard from '../../assets/svg/icon-payment-card-mastercard.svg?vue-component';
import IconAmericanExpress from '../../assets/svg/icon-payment-card-american-express.svg?vue-component';
import IconPaypal from '../../assets/svg/icon-payment-card-paypal.svg?vue-component';
import IconPaypalLogo from '../../assets/svg/icon-paypal-logo.svg?vue-component';
import Loader from '../Loader';
import SecureIcons from './SecureIcons';

export default {
  components: {
    AccountForm,
    LoginForm,
    Payment,
    OrderSummary,
    IconCheck,
    IconVisa,
    IconMastercard,
    IconAmericanExpress,
    IconPaypal,
    IconPaypalLogo,
    Loader,
    SecureIcons,
    Badges,
  },
  data() {
    return {
      tabs: {
        account: {
          name: 'Account',
          id: 1,
        },
        payment: {
          name: 'Payment',
          id: 2,
        },
      },
      loginForm: false,
      accountVerified: this.$store.state.auth.user,
      clickedSection: null,
    };
  },
  head() {
    const { name } = this.order.plan;
    const metaTitle = `Checkout - ${name}`;

    return {
      titleTemplate: `${metaTitle}%s`,
    };
  },
  computed: {
    ...mapState('checkout', ['order', 'paymentInfo', 'loadingChargify']),
    cardIcon() {
      if (this.order.paymentInfo) {
        const { cardType } = this.order.paymentInfo;
        if (['visa', 'discover'].includes(cardType)) {
          return require(`../../assets/svg/icon-payment-card-${cardType}.svg?vue-component`);
        }

        if (cardType === 'master') {
          return require('../../assets/svg/icon-payment-card-mastercard.svg?vue-component');
        }

        if (cardType === 'american_express') {
          return require('../../assets/svg/icon-payment-card-american-express.svg?vue-component');
        }
      }

      return false;
    },
    maskedCardNumber() {
      if (this.order.paymentInfo) {
        return this.order.paymentInfo.maskedCardNumber;
      }

      return '';
    },
    currentTab: {
      get() {
        return this.$store.state.checkout.currentTab;
      },
      set(value) {
        this.dispatchDataEvent(true);
        this.$store.commit('checkout/setCurrentTab', value);
      },
    },
    paymentProfile() {
      return this.order.paymentInfo ? this.order.paymentInfo.paymentProfileId : false;
    },
  },
  mounted() {
    this.$nextTick(() => {
      window.addEventListener('scroll', this.handleScroll);
      this.$once('hook:beforeDestroy', () => window.removeEventListener('scroll', this.handleScroll));
    });

    if (!this.accountVerified) {
      this.$store.commit('checkout/setLoadingChargify', false);
    }

    this.$store.commit('dataLayer/resetRegistered');
    this.dispatchDataEvent();
  },
  methods: {
    handleScroll() {
      if (this.$store.state.layout === 'mobile' && this.clickedSection) {
        const yOffset = this.$config.project === 'lms' ? -64 : -68;
        const element = this.clickedSection;
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        if (y < window.pageYOffset) {
          window.scrollTo({ top: y, behavior: 'instant' });
        }
      }
    },
    changeTab(tab = 'payment', skipCheck = false) {
      if (!skipCheck && (this.currentTab === tab || (tab === 'account' && this.accountVerified) || (tab === 'payment' && !this.$store.state.auth.user))) {
        return;
      }

      this.currentTab = tab;

      if (this.$store.state.layout === 'mobile') {
        setTimeout(() => {
          const step = document.querySelector('.step.active');
          this.scrollToSection(step);
        }, 200);
      }
    },
    scrollToSection(element) {
      const yOffset = this.$config.project === 'lms' ? -64 : -68;
      const y = element.getBoundingClientRect().top + window.scrollY - yOffset;
      this.clickedSection = element;
      window.scrollTo({ top: y, behavior: 'smooth' });

      setTimeout(() => {
        this.clickedSection = null;
      }, 500);
    },
    async loggedSuccessfully() {
      this.accountVerified = true;
      this.$store.commit('checkout/setLoadingChargify', true);
      await this.$store.dispatch('checkout/fetchOrderSummary');
      this.changeTab('payment', true);
    },
    orderSummaryExpanded(expanded) {
      if (expanded) {
        document.querySelector('.steps-holder').classList.add('summary-expanded');
        setTimeout(() => {
          const orderSection = document.querySelector('.order-summary');
          const yOffset = this.$config.project === 'lms' ? -64 : -68;
          window.scrollTo({ top: orderSection.getBoundingClientRect().top + window.pageYOffset + yOffset, behavior: 'smooth' });
        }, 300);
      } else {
        document.querySelector('.steps-holder').classList.remove('summary-expanded');
      }
    },
    dispatchDataEvent(isSwitchedTab) {
      const step = this.tabs[this.currentTab].id;
      let dataLayerData = { event: 'checkoutStep', value: step };

      if ([1, 2].includes(step) && (!isSwitchedTab || (isSwitchedTab && !dataLayer.find((d) => d.event === 'eec.checkout')))) {
        dataLayerData = {
          event: 'checkout',
          value: {
            step,
            planTitle: this.order.plan.name,
            extId: this.order.plan.extId,
            price: this.order.plan.price,
          },
        };
      }

      this.$store.dispatch('dataLayer/addEvent', dataLayerData);
    },
  },
};
</script>

<style lang="scss">
.checkout {
  padding-top: var(--spacing-55);
  color: var(--main-light-shade-4);

  .link-accent {
    font-weight: 700;
  }

  .h2 {
    color: var(--main-light);
  }

  .tabs {
    display: flex;
    align-items: center;
    margin-top: var(--spacing-48);

    .tab {
      display: flex;
      justify-content: center;
      align-items: center;
      color: var(--main-dark-light-3);
      font-weight: 500;

      &.active {
        color: var(--main-light);

        span {
          background-color: var(--accent);
        }
      }

      span {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 24px;
        height: 24px;
        font-size: 14px;
        font-weight: 600;
        margin-right: var(--spacing-8);
        background-color: var(--main-dark-shade-2);
        border-radius: 50%;
      }
    }

    .progress-bar {
      height: 2px;
      width: 64px;
      background-color: var(--main-dark-shade-2);
      margin: 0 var(--spacing-12);

      &.full {
        div {
          width: 100%;
        }
      }

      div {
        background-color: var(--main-light-shade-4);
        width: 50%;
        height: 100%;
      }
    }
  }

  .row {
    margin-top: var(--spacing-24);
    align-items: flex-start;
  }

  .steps-holder {
    .payment-form-loader {
      opacity: 0.75;
      background: var(--main-dark-shade-1);
      z-index: 2;

      svg {
        width: 64px;

        circle[fill] {
          fill: var(--main-light-shade-4) !important;
        }
      }
    }

    .secure-icons .title {
      margin-bottom: var(--spacing-4);
      font-size: 14px;
    }
  }

  .step {
    padding: var(--spacing-36);
    background-color: var(--main-dark-shade-2);
    border-radius: 10px;

    &:first-of-type {
      margin-bottom: var(--spacing-24);

      &:not(.active) {
        .heading {
          div:not(.icon-check),
          a {
            display: none;
          }
        }
      }
    }

    .payment {
      .payment-tabs {
        margin-bottom: var(--spacing-44);
      }

      .payment-card .payment-form .country {
        margin-top: 0;
      }
    }

    &:not(.active) {
      .loader-mask {
        visibility: hidden;
      }

      &.verified {
        .h5 {
          align-items: center;
          display: flex;

          .icon-check {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            background-color: var(--accent);
            margin-right: var(--spacing-12);
            flex: 0 0 auto;

            svg {
              width: 12px;

              path[fill] {
                fill: var(--main-light);
              }
            }
          }
        }

        .payment-verified {
          margin-top: var(--spacing-16);
          margin-left: var(--spacing-36);
          display: flex;
          justify-content: space-between;

          span {
            color: var(--main-dark-light-4);
            cursor: pointer;
          }

          .card {
            display: flex;
            align-items: center;
            color: var(--main-light);

            svg {
              margin-right: 12px;
            }
          }
        }
      }

      .heading {
        margin: 0;
      }

      .content {
        margin: 0;
        max-height: 0;
        overflow: hidden;
        opacity: 0;
      }
    }

    &.active {
      position: relative;

      .heading .h5 {
        color: var(--main-light);
      }
    }

    .heading {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .h5 {
        margin: 0;

        span {
          color: var(--main-dark-light-4);
          cursor: pointer;
          margin-left: 24px;
          font-size: 16px;
        }
      }

      div {
        font-weight: 500;
      }

      .payment-icons {
        display: flex;
        justify-content: center;
        align-items: center;

        svg {
          margin: 0 var(--spacing-4);
          flex: 0 0 auto;
        }

        .paypal {
          border-left: 1px solid var(--main-dark-light-4);
          height: 28px;
          display: flex;
          align-items: center;
          margin-left: 12px;
          padding-left: 12px;
        }
      }
    }

    .content {
      margin-top: var(--spacing-36);
      max-height: 2000px;
      opacity: 1;
      transition: all 500ms ease-in;
    }
  }

  .important-information {
    margin-top: var(--spacing-24);
    padding: var(--spacing-36);
    background-color: var(--main-dark-light-3);
    border-radius: 10px;

    .h5 {
      color: var(--main-light);
    }

    p {
      color: var(--main-light-shade-1);
    }

    .allocation-change-info {
      .h5 {
        margin-top: 60px;
      }

      .current-subscriptions {
        font-size: 14px;
        display: flex;
        align-items: center;
        margin-top: 16px;

        span {
          margin-left: 8px;
          background-color: #4B5461;
          border: 1px solid #565E6A;
          border-radius: 3px;
          width: 54px;
          height: 34px;
          display: inline-block;
          font-size: 18px;
          font-weight: 500;
          text-align: center;
          line-height: 32px;
        }
      }
    }

    &.coins {
      padding: var(--spacing-32);
    }

    .coins-info {
      color: var(--main-light);

      .h6 {
        margin-top: var(--spacing-40);
        font-weight: 400;
      }

      .price-list {
        counter-reset: coin-pricing-counter;
        list-style: none;
        display: flex;
        flex-flow: column wrap;
        padding: 0;
        margin: 0;
        height: 230px;

        li {
          counter-increment: coin-pricing-counter;
          position: relative;
          padding: var(--spacing-8);
          display: flex;
          align-items: center;

          span {
            color: var(--accent);
          }

          &:before {
            content: counter(coin-pricing-counter);
            display: flex;
            align-items: center;
            justify-content: center;
            width: 28px;
            height: 28px;
            border-radius: 10px;
            font-weight: 600;
            font-size: 18px;
            background-color: var(--gray);
            margin-right: var(--spacing-8);
          }
        }
      }
    }
  }
}

@include bp($bp-mobile) {
  .checkout {
    padding-top: var(--spacing-36);

    .h2 {
      font-size: 32px;
    }

    .tabs {
      margin-top: var(--spacing-36);
    }

    .steps-holder {
      padding-bottom: var(--spacing-24);

      .secure-icons {
        display: none;
      }
    }

    .step {
      padding-right: var(--spacing-24);
      padding-left: var(--spacing-24);

      &.active {
        .heading .h5 {
          margin-bottom: var(--spacing-16);

          &.back-button {
            display: flex;
            justify-content: space-between;
            width: 100%;
          }
        }
      }

      &:not(.active) {
        &.verified {
          .payment-verified {
            margin-left: 0;

            .card {
              margin-right: 11px;

              svg {
                flex: 0 0 auto;
              }
            }
          }
        }
      }

      .heading {
        flex-direction: column;
        align-items: flex-start;

        .payment-icons {
          margin-top: var(--spacing-16);
          justify-content: space-between;
          width: 100%;

          svg {
            transform: scale(0.8);
            margin: 0;
          }

          .paypal {
            margin-left: 0;
            padding-left: 0;
          }
        }

        .content {
          margin-top: var(--spacing-24);
        }
      }
    }

    .important-information {
      &:not(.coins) {
        padding-right: var(--spacing-24);
        padding-left: var(--spacing-24);
      }

      .coins-info .price-list {
        flex-wrap: nowrap;
        height: unset;
      }
    }
  }
}

.fa {
  .checkout {
    .link-accent {
      font-weight: 500;
    }

    .step {
      background: var(--main-dark-light-1);
    }
  }

  @include bp($bp-mobile) {
    .checkout {
      .step {
        .heading {
          .payment-icons {
            justify-content: unset;
            margin-bottom: var(--spacing-8);

            svg {
              transform: unset;
              margin: 0 var(--spacing-4);
            }
          }
        }

        &.verified {
          .content {
            margin-top: 0;
          }
        }
      }
    }
  }
}
</style>
